import { uniq } from "lodash";
import { mix } from "polished";
import React, { useRef, useState } from "react";
import { animated, useTransition } from "react-spring";
import styled from "styled-components";
import { layout, LayoutProps, space, SpaceProps } from "styled-system";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import CheckBox from "./Checkbox";
import { Flex } from "./Flex";
import Panel from "./Panel";
import Text from "./Text";

export type MultiSelectProps = {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  defaultLabel?: string;
  disabled?: boolean;
} & SpaceProps &
  LayoutProps;

const Container = styled.div<SpaceProps & LayoutProps>`
  position: relative;
  min-width: 200px;
  ${space};
  ${layout};
`;

const SelectButton = styled.button`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  border: 1px solid ${(props) => props.theme.colors.grey3};
  border-radius: 3px;
  cursor: pointer;
  line-height: 1;
  background-color: ${(props) => props.theme.colors.bg};
  background-image: linear-gradient(
    to top,
    ${(props) => mix(0.02, props.theme.colors.body, props.theme.colors.bg)},
    ${(props) => props.theme.colors.bg} 33%
  );
  align-items: center;
  padding: 8px 12px;
  padding-right: 6px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:after {
    content: "";
    justify-self: end;
    width: 0.7em;
    height: 0.4em;
    margin-top: 0.1em;
    pointer-events: none;
    margin-right: 0.2em;
    margin-left: 0.5em;
    background-color: ${(props) => props.theme.colors.primary};
    clip-path: polygon(100% 0%, 0 0%, 50% 100%);
  }
`;

const OptionsPanel = styled(animated(Panel))`
  position: absolute;
  z-index: 1000;
  left: 0;
  top: calc(100% + 6px);
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  /* popover, not a card — don't inherit large theme panel radii */
  border-radius: 6px;
`;

const Item = styled(Flex)`
  cursor: pointer;
  &:hover {
    background-color: ${(props) =>
      mix(0.8, props.theme.colors.bg, props.theme.colors.primary)};
  }
`;

const DoneRow = styled.button`
  position: sticky;
  bottom: 0;
  width: 100%;
  border: 0;
  border-top: 1px solid ${(props) => props.theme.colors.grey4};
  background-color: ${(props) => props.theme.colors.bg};
  color: ${(props) => props.theme.colors.primary};
  font-weight: 600;
  padding: 8px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      mix(0.9, props.theme.colors.bg, props.theme.colors.primary)};
  }
`;

const springConfig = {
  mass: 1,
  tension: 300,
  friction: 26,
};

export function MultiSelect({
  options,
  value,
  onChange,
  defaultLabel = "<None>",
  disabled,
  ...rest
}: MultiSelectProps) {
  const containerRef = useRef<HTMLDivElement>(null!);
  const buttonRef = useRef<HTMLButtonElement>(null!);
  const [open, setOpen] = useState<boolean>(false);
  const trans = useTransition(open, {
    from: { opacity: 0, y: -80, scaleY: 0.8 },
    enter: { opacity: 1, y: 0, scaleY: 1 },
    leave: { opacity: 0, y: -80, scaleY: 0.8 },
    reverse: open,
    config: springConfig,
  });

  useOnClickOutside(containerRef, () => {
    setOpen(false);
  });

  const toggle = () => setOpen((v) => !v);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  const label = value.length === 0 ? defaultLabel : `${value.length} selected`;

  const setItem = (itemValue: string) => (val: boolean) => {
    if (!open) return;
    if (val) {
      onChange(uniq([...value, itemValue]));
    } else {
      onChange(value.filter((x) => x !== itemValue));
    }
  };

  const toggleItem = (itemValue: string) => () =>
    setItem(itemValue)(!value.includes(itemValue));

  return (
    <Container ref={containerRef} onKeyDown={onKeyDown} {...rest}>
      <SelectButton
        ref={buttonRef}
        onClick={toggle}
        disabled={disabled}
        type="button"
      >
        {label}
      </SelectButton>
      {trans(
        (styles, item) =>
          item && (
            <OptionsPanel
              color="none"
              boxShadow={2}
              style={{
                ...styles,
                pointerEvents: !open ? "none" : undefined,
              }}
              p={0}
              role={"listbox"}
              aria-multiselectable
            >
              {options.map((x) => (
                <Item
                  tabIndex={0}
                  alignItems="center"
                  p={1}
                  role={"option"}
                  aria-selected={value.includes(x.value)}
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setItem(x.value)(!value.includes(x.value));
                    }
                  }}
                  onClick={toggleItem(x.value)}
                  key={x.value}
                >
                  <CheckBox mb={0} mr={2} value={value.includes(x.value)} />
                  <Text fontWeight={500}>{x.label}</Text>
                </Item>
              ))}
              <DoneRow type="button" onClick={close}>
                Done
              </DoneRow>
            </OptionsPanel>
          ),
      )}
    </Container>
  );
}
